// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { ITheme } from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { getTheme, loadTheme } from "@fluentui/react";

import {
  defaultDarkTheme,
  defaultLightTheme,
  defaultDarkThemeNext,
  defaultLightThemeNext,
  defaultHighContrastThemeNext,
} from "./DefaultThemes";
import type { CustomAppTheme } from "../../CustomAppTheme";

// Theme types supported for the users (the number of options here may grow in the future).
export enum AppTheme {
  Light,
  Dark,
  HighContrast,
}

// This class will manage the Themes offered to the users by the App.
// It allow developers to define themes that are stored in a dictionary, and
// then these can be used to load the Theme to the UI and update the UI controls.
export class AppThemeManager {
  private static instance: AppThemeManager;

  private AppThemeList: { [key in AppTheme]: CustomAppTheme } = {
    [AppTheme.Light]: defaultLightThemeNext,
    [AppTheme.Dark]: defaultDarkThemeNext,
    [AppTheme.HighContrast]: defaultHighContrastThemeNext,
  };

  private AppThemeListV8: { [key in AppTheme]: ITheme } = {
    [AppTheme.Light]: defaultLightTheme,
    [AppTheme.Dark]: defaultDarkTheme,
    [AppTheme.HighContrast]: defaultDarkTheme,
  };

  private currentTheme: AppTheme = AppTheme.Light;
  private themeChangedCallbacks: Array<() => void> = [];
  private themingIsInitialized: boolean = false;
  private fluentThemingIsInitialized: boolean = false;

  private readonly onThemingInitialized: () => void;
  private readonly themingInitializePromise: Promise<void>;

  // Singleton
  public static getInstance(): AppThemeManager {
    if (!AppThemeManager.instance) {
      AppThemeManager.instance = new AppThemeManager();
    }
    return AppThemeManager.instance;
  }

  constructor() {
    let resolveThemingInitialize: () => void;

    this.themingInitializePromise = new Promise((resolve) => {
      resolveThemingInitialize = resolve;
    });

    this.onThemingInitialized = () => {
      this.themingIsInitialized = true;
      resolveThemingInitialize();
    };
  }

  // Returns a boolean indicating if an initial theme has been loaded to the UX by this AppThemeManager
  public isThemingInitialized(): boolean {
    return this.themingIsInitialized;
  }

  /**
   * Wait for this AppThemeManager to load any theme
   */
  public async waitForInitialThemeLoad(): Promise<void> {
    return this.themingInitializePromise;
  }

  // Returns a boolean indicating if Fluent returns a theme when we call its getTheme function
  public isFluentThemingInitialized(): boolean {
    if (!this.fluentThemingIsInitialized) {
      const fluentTheme = getTheme();
      if (fluentTheme === undefined || fluentTheme === null) {
        return false;
      }
      this.fluentThemingIsInitialized = true;
    }
    return true;
  }

  /* This method allows a caller to override acui-theme's internal validation for if a theme has been initialized correctly,
   and allows the consumer of this package to still attempt to use themed styling with the acui-* components without having set a theme here.
   This is not the intended or expected usage of this solution, but since there were several consumers engaging in that approach, we are keeping
   this here for backwards compatibility sake. If you have questions, contact the 'wisetheming' alias. */
  public overrideThemingIsInitialized(isEnabled: boolean) {
    if (isEnabled) {
      this.onThemingInitialized();
    } else {
      this.themingIsInitialized = false;
    }
  }

  // Get current Theme mode (as an AppTheme).
  // For example: if you just want to know if we are in dark mode or not.
  public getThemeMode(): AppTheme {
    return this.currentTheme;
  }

  // Get the current Theme (as the full ITheme).
  // Future note: If we end up storing the v8 theme colors in vnext theme, we could
  // call convertTheme here to get the correct theme.
  public getTheme(): ITheme {
    if (
      this.AppThemeListV8[this.currentTheme] === undefined ||
      this.AppThemeListV8[this.currentTheme] === null
    ) {
      // fallback to the current Fluent theme, if themes have not been set in this package
      return getTheme();
    }
    return this.AppThemeListV8[this.currentTheme];
  }

  public getThemeNext(): CustomAppTheme {
    return this.AppThemeList[this.currentTheme];
  }

  // Set a new Theme definition that can be used by the App.
  // Note that this adds a theme to the dictionary, it does not load it to the UI.
  public setTheme(
    appTheme: AppTheme,
    v8Theme: ITheme,
    newTheme?: CustomAppTheme
  ) {
    // Save the new Theme definition
    if (newTheme) {
      this.AppThemeList[appTheme] = newTheme;
    }

    this.AppThemeListV8[appTheme] = v8Theme;
  }

  // Load a given Theme to the UI (update current theme and re-render all consumer controls).
  public loadTheme(appTheme: AppTheme) {
    this.currentTheme = appTheme;
    // Load the new theme to the UI (this will update the current global Fabric theme, as well as
    // trigger re-renders in all controls set as consumers of the Theme).
    // Future note: If we end up storing the v8 theme colors in vnext theme, we could
    // call convertTheme here to load the correct theme.
    loadTheme(this.AppThemeListV8[appTheme]);
    this.themeChangedCallbacks.forEach((callback) => callback());

    // We consider theming initialized if any theme has been loaded to the UX
    this.onThemingInitialized();
  }

  // Returns requested theme, for when we need to access the palette
  // for a non-active theme (e.g. header initial load)
  // Future note: If we end up storing the v8 theme colors in vnext theme, we could
  // call convertTheme here to get the correct theme.
  public getThemeForMode(theme: AppTheme): ITheme {
    return this.AppThemeListV8[theme];
  }

  // Returns requested theme, for when we need to access the palette
  // for a non-active theme (e.g. header initial load)
  // Future note: If we end up storing the v8 theme colors in vnext theme, we could
  // call convertTheme here to get the correct theme.
  public getThemeForModeNext(theme: AppTheme): CustomAppTheme {
    return this.AppThemeList[theme];
  }

  // Eventually we will create an actual method that converts the vnext theme to one for
  // v8, but until we have full themes from design, we will use this for now
  // we will be plugging the legacy v8 theme colors into our vnexttheme
  // so then we can create a theme here by pulling those colors
  public convertVNextThemeToV8Theme(_theme?: CustomAppTheme): ITheme {
    if (this.currentTheme === AppTheme.Light) {
      return this.AppThemeListV8[AppTheme.Light];
    }
    return this.AppThemeListV8[AppTheme.Dark];
  }

  public registerOnThemeChanged(callback: () => void) {
    if (this.themeChangedCallbacks.indexOf(callback) === -1) {
      this.themeChangedCallbacks.push(callback);
    }
  }
  public unregisterOnThemeChanged(callback: () => void) {
    const i = this.themeChangedCallbacks.indexOf(callback);
    if (i === -1) {
      return;
    }
    this.themeChangedCallbacks.splice(i, 1);
  }
}

/* This function used to be a part of ThemingExperiments and reported if the Theming flight was on.
All flights have shipped, and we would like to remove this as well, together with any fallback hardcoded colors that still exist.
But since we can't yet guarantee that all apps that use this code are supporting Theming, we must keep this here.
It now checks if Theming has been initialized in this package (meaning a theme was saved and loaded to the UI, using setTheme and loadTheme respectively, as expected).
Because several partners using this package were not initializing things here, we have added the support for the case
where we haven't been given a theme in this package but Fluent seems to have one initialized and loaded. */
export function isThemingEnabled(): boolean {
  return (
    AppThemeManager.getInstance().isThemingInitialized() ||
    AppThemeManager.getInstance().isFluentThemingInitialized()
  );
}

/* This method allows a caller to override acui-theme's internal validation for if a theme has been initialized correctly,
and allows the consumer of this package to still attempt to use themed styling with the acui-* components without having set a theme here.
This is not the intended or expected usage of this solution, but since there were several consumer engaging in that approach, we are keeping
this here for backwards compatibility sake. If you have questions, contact the 'wisetheming' alias.
We still hope to remove this method whenever possible in the future, as this was never intended to be used this way, it was originally simply a flight setting method. */
export const setThemingIsEnabled = (isEnabled: boolean) => {
  AppThemeManager.getInstance().overrideThemingIsInitialized(isEnabled);
};
